import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { getVtexConfig } from "@decocms/apps-vtex";
import { legacyProductList } from "@decocms/apps-vtex/loaders/legacy";
import type { Product } from "@decocms/apps-commerce/types";
import { parseCookieValue } from "~/utils/cookies";
import { VISITOR_ID_COOKIE } from "~/utils/visitorId";
import { CRM_BASE_URL, logIfNotOk, trackingTokenHeader } from "~/server/crm";

/**
 * Proxy de recomendação pública pro CRM próprio. Ver
 * docs/superpowers/specs/2026-08-12-cookie-navegacao-eventos-design.md.
 *
 * ATENÇÃO: este arquivo é importado por componentes de CLIENTE
 * (PersonalizedShelf) por causa do stub que o `createServerFn` gera -- ele
 * ENTRA no bundle do navegador. Nada aqui pode ter import estático de módulo
 * server-only (ex: `cloudflare:workers`), senão o `npm run build` quebra.
 * Ver o comentário no topo de `~/server/crm`.
 *
 * O proxy `carrinho-iniciado` NÃO mora mais aqui: virou Server Route de
 * arquivo (`src/routes/tracking/carrinho-iniciado.ts`), porque o fetch do
 * `createServerFn` não usa `keepalive` e era abortado pela navegação pro
 * checkout que acontece no mesmo clique. Ver Finding 4 da revisão final.
 */

function getVisitorIdFromRequest(): string | null {
  return parseCookieValue(getRequestHeader("cookie") ?? "", VISITOR_ID_COOKIE);
}

export interface RecomendacaoPublica {
  categoriasTop: string[];
  produtos: Product[];
}

const EMPTY_RECOMENDACAO: RecomendacaoPublica = { categoriasTop: [], produtos: [] };

/**
 * Nunca lança -- qualquer falha (sem cookie, CRM fora do ar, token
 * inválido, hidratação VTEX falhando) cai no fallback vazio, e
 * PersonalizedShelf (Task 8) já lida com isso não renderizando a seção.
 *
 * Handler anotado com `Promise<any>` (e o cast `as unknown as` na
 * exportação abaixo) pelo mesmo motivo documentado em
 * `invoke.gen.ts`/`.cursor/skills/deco-server-functions-invoke/`: `Product`
 * (via `Offer.teasers[].generalValues?: unknown`) não satisfaz a checagem
 * de serializabilidade estrita do `createServerFn` -- `unknown` não é um
 * dos tipos em `Serializable`/`DefaultSerializable` do
 * `@tanstack/router-core`, então o tipo de retorno real precisa ser
 * "escondido" do `.handler()` e reafirmado só na exportação.
 */
const $recomendacaoPublica = createServerFn({ method: "GET" }).handler(
  async (): Promise<any> => {
    const visitorId = getVisitorIdFromRequest();
    if (!visitorId) return EMPTY_RECOMENDACAO;

    try {
      const resp = await fetch(
        `${CRM_BASE_URL}/api/recomendacao/publica?visitor_id=${encodeURIComponent(visitorId)}`,
        { headers: await trackingTokenHeader() },
      );
      if (!resp.ok) {
        await logIfNotOk("recomendacaoPublica", resp);
        return EMPTY_RECOMENDACAO;
      }

      const json = (await resp.json()) as {
        categorias_top?: string[];
        produtos_recentes?: Array<{ sku?: string }>;
      };

      const skuIds = (json.produtos_recentes ?? [])
        .map((p) => p.sku)
        .filter((sku): sku is string => Boolean(sku));

      if (skuIds.length === 0) {
        return { categoriasTop: json.categorias_top ?? [], produtos: [] };
      }

      const config = getVtexConfig();
      const baseUrl = config.publicUrl
        ? `https://${config.publicUrl}`
        : `https://${config.account}.vtexcommercestable.${config.domain ?? "com.br"}`;

      const produtos = await legacyProductList({
        query: { skuIds },
        baseUrl,
        priceCurrency: "BRL",
      });

      return { categoriasTop: json.categorias_top ?? [], produtos: produtos ?? [] };
    } catch (error) {
      console.error("[recomendacaoPublica] proxy error:", error);
      return EMPTY_RECOMENDACAO;
    }
  },
);

export const recomendacaoPublica = $recomendacaoPublica as unknown as () =>
  Promise<RecomendacaoPublica>;
